/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FeatureCodesPage.tsx
 🗓️ Created: 2025-01-22
 🧩 Description:
    Feature Codes management page - simplified using hooks and API modules.
────────────────────────────────────────────────────────────────*/

import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Server, ChevronRight, RotateCcw, Maximize2, Minimize2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useContextStore } from '../../store/context';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModuleFormHeader, ModuleFormLayout } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useFeatureCodes } from './hooks/useFeatureCodes';
import { useFeatureCodeModifications } from './hooks/useFeatureCodeModifications';
import { getTranslatedFeatureCodeName } from './utils/translationHelpers';
import type { FeatureCode } from './types';

const FeatureCodesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { selectedTenant, selectedDomain, availableDomains } = useContextStore();
  // Domain name is always a valid string; numeric id may be NaN (Sqid-encoded)
  const domainName = selectedDomain?.domain || availableDomains[0]?.domain || undefined;
  const domainId = selectedDomain?.id && !isNaN(selectedDomain.id) ? selectedDomain.id : undefined;
  const tenantSlug = selectedTenant?.slug || user?.tenant?.slug || 'main';

  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Use hooks for data and modifications
  const {
    categories,
    featureCodes,
    originalValues,
    loading,
    searchQuery,
    setSearchQuery,
    filteredCategories,
    getFeatureCodesByCategory,
    filterFeatureCodes,
    refresh,
  } = useFeatureCodes({
    tenantSlug,
    userId: user?.id,
    domainId,
    domainName,
  });

  const {
    saving,
    hasChanges,
    getEffectiveValue,
    isModified,
    handleFieldChange,
    handleReset,
    handleSave,
  } = useFeatureCodeModifications({
    featureCodes,
    originalValues,
    tenantSlug,
    userId: user?.id,
    onSaveSuccess: refresh,
  });

  // Toggle category expansion
  const toggleCategory = useCallback((categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // Toggle all categories
  const handleToggleAll = useCallback(() => {
    const allCategoryIds = categories.map((cat) => cat.id);
    const allExpanded = categories.length > 0 && categories.every((cat) => expandedCategories.has(cat.id));

    if (allExpanded) {
      setExpandedCategories(new Set());
    } else {
      setExpandedCategories(new Set(allCategoryIds));
    }
  }, [categories, expandedCategories]);

  // Check if all categories are expanded
  const areAllExpanded = useMemo(() => {
    return categories.length > 0 && categories.every((cat) => expandedCategories.has(cat.id));
  }, [categories, expandedCategories]);

  const handleCancel = useCallback(() => {
    if (hasChanges) {
      setShowCancelModal(true);
    } else {
      navigate('/pbx-applications');
    }
  }, [hasChanges, navigate]);

  const confirmCancel = useCallback(() => {
    setShowCancelModal(false);
    navigate('/pbx-applications');
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  // Guard: domain is required for feature codes (domain-specific numbering)
  if (!domainName) {
    // If tenant is selected, domains are still being loaded asynchronously from the header
    if (selectedTenant) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Server className="h-16 w-16 text-muted-foreground" />
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {t('featureCodes.noDomainSelected', 'No Domain Selected')}
          </h2>
          <p className="text-muted-foreground">
            {t('featureCodes.selectDomainMessage', 'Please select a telephony domain from the header to manage feature codes.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Server className="h-5 w-5 text-primary" />}
        title={t('featureCodes.title')}
        backButtonText={t('common.exit')}
        onBack={handleCancel}
        searchInput={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('featureCodes.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-10 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            )}
          </div>
        }
        actions={[
          {
            icon: areAllExpanded ? (
              <Minimize2 className="h-5 w-5 text-muted-foreground" />
            ) : (
              <Maximize2 className="h-5 w-5 text-muted-foreground" />
            ),
            tooltip: areAllExpanded ? t('common.collapseAll') : t('common.expandAll'),
            onClick: handleToggleAll,
          },
        ]}
      />

      <div className="mt-8">
        <ModuleFormLayout
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          onCancel={handleCancel}
          isSaving={saving}
          saveText={saving ? t('common.saving') : t('common.save')}
          cancelText={t('common.cancel')}
          showSave={hasChanges}
        >
          {/* Categories Accordion */}
          <div className="space-y-3">
            {filteredCategories.map((category) => {
              const isExpanded = expandedCategories.has(category.id);
              const categoryCodes = filterFeatureCodes(getFeatureCodesByCategory(category.id));

              return (
                <div key={category.id} className="border border-border rounded-lg bg-card">
                  {/* Category Header */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent/30 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                        <Server className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground">
                          {t(`featureCodes.categories.${category.name.toLowerCase().replace(/\s+/g, '_')}`, category.name)}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {categoryCodes.length} {t('featureCodes.featureCodes')}
                        </p>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Category Content */}
                  <motion.div
                    initial={false}
                    animate={{
                      height: isExpanded ? 'auto' : 0,
                      opacity: isExpanded ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-border">
                      <div className="overflow-x-auto">
                        <table className="w-full table-fixed">
                          <thead className="bg-accent/50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground w-1/2">
                                {t('featureCodes.featureCode')}
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground w-1/4">
                                {t('featureCodes.number')}
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-semibold text-foreground w-20">
                                {t('common.actions')}
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-semibold text-foreground w-20">
                                {t('common.enabled')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {categoryCodes.map((featureCode: FeatureCode) => {
                              const featureIdentifier = featureCode.uuid || featureCode.id.toString();
                              const modified = isModified(featureIdentifier);
                              const currentNumber =
                                getEffectiveValue(featureCode, 'custom_number') ||
                                getEffectiveValue(featureCode, 'default_number');

                              return (
                                <tr key={featureCode.id} className="hover:bg-accent/30">
                                  <td className="px-4 py-3">
                                    <SimpleTooltip
                                      content={featureCode.description || getTranslatedFeatureCodeName(featureCode, t)}
                                    >
                                      <span className="text-sm font-medium text-foreground">
                                        {getTranslatedFeatureCodeName(featureCode, t)}
                                      </span>
                                    </SimpleTooltip>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="relative">
                                      <input
                                        type="text"
                                        value={currentNumber}
                                        onChange={(e) => handleFieldChange(featureIdentifier, 'custom_number', e.target.value)}
                                        className={`w-full px-3 py-1.5 border bg-background text-foreground focus:ring-1 focus:ring-primary focus:border-transparent rounded-md font-mono text-sm ${
                                          modified
                                            ? 'border-primary ring-1 ring-primary/20'
                                            : 'border-border'
                                        }`}
                                      />
                                      {modified && (
                                        <div
                                          className="absolute right-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary rounded-full"
                                          title={t('common.modified')}
                                        />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex justify-center">
                                      <SimpleTooltip content={t('common.reset')}>
                                        <button
                                          type="button"
                                          onClick={() => handleReset(featureIdentifier)}
                                          disabled={!modified}
                                          className={`p-1.5 rounded-md transition-colors ${
                                            modified
                                              ? 'text-primary hover:bg-primary/10'
                                              : 'text-muted-foreground/30 cursor-not-allowed'
                                          }`}
                                        >
                                          <RotateCcw className="w-4 h-4" />
                                        </button>
                                      </SimpleTooltip>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex justify-center">
                                      <Toggle
                                        value={getEffectiveValue(featureCode, 'enabled')}
                                        onChange={(value) => handleFieldChange(featureIdentifier, 'enabled', value)}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {categoryCodes.length === 0 && (
                              <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                                  {t('featureCodes.noFeatureCodes')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </ModuleFormLayout>
      </div>

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        title={t('common.confirmCancel', 'Discard Changes?')}
        message={t('common.unsavedChanges', 'You have unsaved changes. Are you sure you want to discard them?')}
        confirmText={t('common.discard', 'Discard')}
        cancelText={t('common.keepEditing', 'Keep Editing')}
        type="warning"
      />
    </div>
  );
};

export default FeatureCodesPage;
